import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_CONFIG from '../../apiConfig';
import { getSubscriptionStatus, SubscriptionType, isProPlan, isEnterprisePlan } from '../../services/subscriptionService';
import { upscaleImageAndReturn } from '../../services/upscalerService';
import SubscriptionGuard from '../ui/SubscriptionGuard';
import { useCreditEstimation } from '../../hooks/useCreditEstimation';
import CreditCostPreview from '../ui/CreditCostPreview';
import InsufficientCreditsModal from '../ui/InsufficientCreditsModal';
import CustomSelect from '../ui/CustomSelect';

type EditorTool = 'upscaler' | 'background-removal' | 'video-generation';

interface VideoTemplate {
  videoUrl: string;
  videoId: string;
  duration: number;
  type: string;
}

interface VideoTemplatesResponse {
  success: boolean;
  videoTemplates: VideoTemplate[];
  message: string;
  statusCode: number;
}

interface LocationState {
  inputImages: string[];
  prompt: string;
  style: string;
  variations: number;
  recreatingFrom: string;
  generatedImages?: string[];
  videoUrlList?: string[];
  tool?: EditorTool;
}

const CanvasEditor: React.FC = () => {
  const location = useLocation();
  const state = location.state as LocationState;

  const [selectedTool, setSelectedTool] = useState<EditorTool>('video-generation');
  const [inputImage, setInputImage] = useState<string | null>(null);
  const [generatedResult, setGeneratedResult] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [generationTime, setGenerationTime] = useState<number>(0);
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);
  const [showSubscriptionGuard, setShowSubscriptionGuard] = useState(false);
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [selectedVideoTemplate, setSelectedVideoTemplate] = useState<string>('');
  const [videoTemplates, setVideoTemplates] = useState<VideoTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [playingVideos, setPlayingVideos] = useState<Set<string>>(new Set());
  const [selectedMode, setSelectedMode] = useState<string>('balanced');
  const [selectedDuration, setSelectedDuration] = useState<number>(5);
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [selectedResolution, setSelectedResolution] = useState<string>('4K');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { currentUser, refreshUserCredits, userCredits } = useAuth();

  // Credit estimation hooks for both video and upscaler
  const [videoCreditEstimation, estimateVideoCost] = useCreditEstimation('video');
  const [upscalerCreditEstimation, estimateUpscalerCost] = useCreditEstimation('upscaler');
  const [backgroundRemovalCreditEstimation, estimateBackgroundRemovalCost] = useCreditEstimation('background-removal');

  // Get the appropriate credit estimation based on selected tool
  const currentCreditEstimation = selectedTool === 'video-generation'
    ? videoCreditEstimation
    : selectedTool === 'upscaler'
      ? upscalerCreditEstimation
      : selectedTool === 'background-removal'
        ? backgroundRemovalCreditEstimation
        : { creditCost: null, isEstimating: false, error: null, canAfford: true };

  // Function to fetch video templates from API
  const fetchVideoTemplates = async () => {
    setIsLoadingTemplates(true);
    setTemplatesError(null);

    try {
      const response = await fetch(API_CONFIG.VIDEO_TEMPLATES_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'tryon'
        }),
      });

      // Check HTTP status
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: VideoTemplatesResponse = await response.json();

      if (data.success && data.videoTemplates) {
        // Sort templates by videoId number
        const sortedTemplates = data.videoTemplates.sort((a, b) => {
          const aId = parseInt(a.videoId) || 0;
          const bId = parseInt(b.videoId) || 0;
          return aId - bId;
        });

        setVideoTemplates(sortedTemplates);
        // No default template selection - user must choose manually
      } else {
        throw new Error(data.message || 'Failed to fetch video templates');
      }
    } catch (error) {
      console.error('Failed to fetch video templates:', error);
      setTemplatesError(error instanceof Error ? error.message : 'Failed to load templates');
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  // Effect to fetch video templates when component mounts
  useEffect(() => {
    fetchVideoTemplates();
  }, []);

  // Effect to handle navigation state from Gallery or Dashboard
  useEffect(() => {
    // Read tool from URL query parameter first, then from state
    const searchParams = new URLSearchParams(location.search);
    const toolFromUrl = searchParams.get('tool') as EditorTool | null;
    
    // Handle tool selection from URL query parameter or navigation state
    if (toolFromUrl && ['video-generation', 'upscaler', 'background-removal'].includes(toolFromUrl)) {
      setSelectedTool(toolFromUrl);
      
      // Set default prompts based on tool from URL
      if (toolFromUrl === 'upscaler' || toolFromUrl === 'background-removal' || toolFromUrl === 'video-generation') {
        setPrompt('');
      }
    } else if (state?.tool) {
      setSelectedTool(state.tool);
    }

    if (state?.inputImages && state.inputImages.length > 0) {
      // Load the input image
      setInputImage(state.inputImages[0]);
      setUploadStatus('Image loaded');

      // Load the prompt if available, or set default for video generation
      if (state.prompt) {
        setPrompt(state.prompt);
      } else if (state.tool === 'upscaler') {
        // Clear prompt for upscaler tool
        setPrompt('');
      } else if (selectedTool === 'video-generation' || state.tool === 'video-generation' || toolFromUrl === 'video-generation') {
        setPrompt('');
      }

      // Load generated result if available
      // For video generation, use videoUrlList; for others, use generatedImages
      if (state.videoUrlList && state.videoUrlList.length > 0) {
        setGeneratedResult(state.videoUrlList[0]);
        // Set video loading state when coming from Gallery
        setIsVideoLoading(true);
      } else if (state.generatedImages && state.generatedImages.length > 0) {
        setGeneratedResult(state.generatedImages[0]);
      }
    } else if (state?.tool) {
      // Handle tool-specific prompt setup when coming from Dashboard (no input images)
      if (state.tool === 'upscaler' || state.tool === 'background-removal' || state.tool === 'video-generation') {
        // Clear prompt for all tools by default
        setPrompt('');
      }
    }
  }, [state, location.search]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [timerInterval]);

  // Allow natural scrolling - removed scroll prevention

  // Credit estimation effect - trigger when generation settings change
  useEffect(() => {
    // Only estimate if we have required data and user is authenticated
    if (!currentUser?.uid || !inputImage || inputImage.startsWith('data:')) {
      return;
    }

    if (selectedTool === 'video-generation') {
      // Build payload for video generation estimation
      const estimationPayload = {
        inputImageUrls: [inputImage],
        inputVideoUrl: selectedVideoTemplate ? videoTemplates.find(t => t.videoId === selectedVideoTemplate)?.videoUrl : undefined,
        prompt: selectedVideoTemplate ? "" : (prompt || 'showing off clothes to camera in different poses'),
        duration: selectedDuration,
        resolution: selectedMode === 'quality' ? '720p' : selectedMode === 'performance' ? '360p' : '480p',
        mode: selectedMode,
        templateId: selectedVideoTemplate || undefined
      };
      estimateVideoCost(estimationPayload);
    } else if (selectedTool === 'upscaler') {
      // Build payload for upscaler estimation
      const estimationPayload = {
        imageUrl: inputImage,
        quality: "high",
        upscaleFactor: selectedResolution === '2K' ? '2x' : selectedResolution === '4K' ? '4x' : '8x',
        targetResolution: selectedResolution === '2K' ? '1440p' : selectedResolution === '4K' ? '2160p' : '4320p'
      };
      estimateUpscalerCost(estimationPayload);
    } else if (selectedTool === 'background-removal') {
      // Build payload for background removal estimation
      const estimationPayload = {
        userId: currentUser?.uid,
        imageUrl: inputImage
      };
      estimateBackgroundRemovalCost(estimationPayload);
    }
  }, [
    currentUser?.uid,
    inputImage,
    selectedTool,
    selectedMode,
    selectedDuration,
    selectedVideoTemplate,
    videoTemplates,
    prompt,
    selectedResolution,
    estimateVideoCost,
    estimateUpscalerCost,
    estimateBackgroundRemovalCost
  ]);

  const startTimer = () => {
    setGenerationTime(0);
    const interval = setInterval(() => {
      setGenerationTime(prev => prev + 0.1);
    }, 100);
    setTimerInterval(interval);
  };

  const stopTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
  };

  const toolOptions = [
    { value: 'video-generation', label: 'Video Generation', disabled: false },
    { value: 'upscaler', label: 'Upscaler', disabled: false },
    { value: 'background-removal', label: 'Background Removal', disabled: false }
  ];

  const promptSuggestions = [
    'showing off clothes to camera in different poses',
    'elegant fashion runway walk with confident poses',
    'casual lifestyle product demonstration',
    'professional commercial product showcase',
    'dynamic movement highlighting product features',
    'artistic fashion photography style poses',
    'natural everyday use demonstration'
  ];

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];

    // Show immediate preview while uploading
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setInputImage(e.target.result as string);
      }
    };
    reader.readAsDataURL(file);

    // Upload to API
    setIsUploading(true);
    setUploadStatus('Uploading image...');
    setGeneratedResult(null); // Reset result when new image is uploaded

    const formData = new FormData();
    formData.append('files', file);
    formData.append('userId', currentUser?.uid || 'anonymous');

    try {
      console.log('Uploading to:', API_CONFIG.CANVAS_UPLOAD_URL);
      const response = await fetch(API_CONFIG.CANVAS_UPLOAD_URL, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();

        // Handle different response formats
        let uploadedUrl = null;
        if (data.uploaded && Array.isArray(data.uploaded) && data.uploaded.length > 0) {
          uploadedUrl = data.uploaded[0].url;
        } else if (data.urls && Array.isArray(data.urls) && data.urls.length > 0) {
          uploadedUrl = data.urls[0];
        } else if (data.url) {
          uploadedUrl = data.url;
        }

        if (uploadedUrl) {
          setInputImage(uploadedUrl);
          setUploadStatus('Image uploaded successfully!');
        } else {
          setUploadStatus('Upload successful, but no URL returned');
        }
      } else {
        setUploadStatus('Upload failed. Please try again.');
        console.error('Upload failed:', response.statusText);
      }
    } catch (error) {
      setUploadStatus('Upload failed. Please try again.');
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileUpload(e.dataTransfer.files);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileUpload(e.target.files);
  };

  const handleGenerate = async () => {
    if (!inputImage) {
      alert('Please upload an image first');
      return;
    }

    // Check if image is still base64 (not yet uploaded to server)
    if (inputImage.startsWith('data:')) {
      alert('Please wait for the image to finish uploading before generating');
      return;
    }

    if (!currentUser) {
      alert('Please log in to generate content');
      return;
    }

    // Check if user can afford the generation
    if (!currentCreditEstimation.canAfford) {
      setShowInsufficientCreditsModal(true);
      return;
    }

    // Check subscription status for Canvas Editor tools
    // Upscaler is available to all users, so only guard the other tools
    if (selectedTool === 'background-removal' || selectedTool === 'video-generation') {
      try {
        const subscriptionData = await getSubscriptionStatus(currentUser.uid);

        // For background-removal and video-generation, require Pro or Enterprise
        if (!isProPlan(subscriptionData.type) && !isEnterprisePlan(subscriptionData.type)) {
          setShowSubscriptionGuard(true);
          return;
        }
      } catch (error) {
        console.error('Failed to check subscription status:', error);
        alert('Unable to verify subscription status. Please try again.');
        return;
      }
    }

    setIsProcessing(true);
    setGeneratedResult(null); // Reset result when starting new generation
    startTimer(); // Start the timer

    try {
      if (selectedTool === 'video-generation') {
        // Call video generation API
        const response = await fetch(API_CONFIG.VIDEO_GENERATE_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: currentUser.uid,
            inputImageUrls: [inputImage],
            inputVideoUrl: selectedVideoTemplate ? videoTemplates.find(t => t.videoId === selectedVideoTemplate)?.videoUrl : undefined,
            prompt: selectedVideoTemplate ? "" : (prompt || ''),
            duration: selectedDuration,
            resolution: selectedMode === 'quality' ? '720p' : selectedMode === 'performance' ? '360p' : '480p',
            templateId: selectedVideoTemplate || undefined,
            mode: selectedMode
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success && data.videoUrlList && data.videoUrlList.length > 0) {
          setGeneratedResult(data.videoUrlList[0]);
        } else {
          throw new Error(data.message || 'Video generation failed');
        }
      } else if (selectedTool === 'upscaler') {
        // Call upscaler service
        const upscaledImageUrl = await upscaleImageAndReturn(
          inputImage,
          currentUser.uid,
          refreshUserCredits,
          selectedResolution === '2K' ? '2x' : selectedResolution === '4K' ? '4x' : '8x',
          selectedResolution === '2K' ? '1440p' : selectedResolution === '4K' ? '2160p' : '4320p'
        );
        setGeneratedResult(upscaledImageUrl);
      } else if (selectedTool === 'background-removal') {
        // Call background removal API
        const response = await fetch(API_CONFIG.BACKGROUND_REMOVAL_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: currentUser.uid,
            imageUrl: inputImage
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success && data.imageUrls && data.imageUrls.length > 0) {
          setGeneratedResult(data.imageUrls[0]);
        } else {
          throw new Error(data.message || 'Background removal failed');
        }
      }
    } catch (error) {
      console.error('Generation failed:', error);
      alert(`Generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      stopTimer(); // Stop the timer
      setIsProcessing(false);
    }
  };

  const getToolDescription = (tool: EditorTool) => {
    switch (tool) {
      case 'upscaler':
        return 'Enhance and upscale your image quality using AI';
      case 'background-removal':
        return 'Remove or replace the background of your image using AI';
      case 'video-generation':
        return '';
      default:
        return '';
    }
  };

  const handleToolChange = (value: string) => {
    const selectedOption = toolOptions.find(option => option.value === value);
    if (selectedOption && !selectedOption.disabled) {
      setSelectedTool(value as EditorTool);
    }
  };

  const handleAutogeneratePrompt = async () => {
    if (!currentUser?.uid || !inputImage) {
      alert('Please upload an image first');
      return;
    }

    // Check if image is still base64 (not yet uploaded to server)
    if (inputImage.startsWith('data:')) {
      alert('Please wait for the image to finish uploading before generating prompt');
      return;
    }

    setIsGeneratingPrompt(true);

    try {
      const response = await fetch(API_CONFIG.VIDEO_PROMPT_GENERATE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: currentUser.uid,
          imageUrl: inputImage,
          model: selectedMode
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.prompt) {
        setPrompt(data.prompt);
        // Clear template selection when using auto-generated prompt
        setSelectedVideoTemplate('');
      } else {
        throw new Error(data.message || 'Failed to generate prompt');
      }
    } catch (error) {
      console.error('Failed to generate prompt:', error);
      alert(`Failed to generate prompt: ${error instanceof Error ? error.message : 'Unknown error'}`);

      // Fallback to random prompt from suggestions
      const randomPrompt = promptSuggestions[Math.floor(Math.random() * promptSuggestions.length)];
      setPrompt(randomPrompt);
    } finally {
      setIsGeneratingPrompt(false);
    }
  };

  return (
    <div className="w-full relative">
      {/* Main Content Area - Three Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 p-2">
        {/* First Column - Image Upload */}
        <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col">
          <div className="p-4 border-b border-accent-taupe flex-shrink-0">
            <h2 className="text-lg font-semibold text-text-heading">Input Image</h2>
            <span className="text-sm text-text-description">Upload an image to get started</span>
          </div>

          <div className="p-4 flex flex-col min-h-[400px]">
            {inputImage ? (
              <div className="relative flex-1">
                <img
                  src={inputImage}
                  alt="Input"
                  className="w-full h-full object-contain bg-accent-sand rounded-lg border border-accent-taupe"
                />
                <button
                  onClick={() => {
                    setInputImage(null);
                    setGeneratedResult(null);
                    setUploadStatus(null);
                    setIsUploading(false);
                  }}
                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg"
                  title="Remove image"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <div
                className={`border-2 border-dashed border-accent-taupe rounded-lg p-8 text-center transition-colors flex-1 flex flex-col items-center justify-center ${isUploading ? 'cursor-not-allowed opacity-70' : 'hover:border-gray-400 cursor-pointer'
                  }`}
                onDrop={isUploading ? undefined : handleDrop}
                onDragOver={isUploading ? undefined : handleDragOver}
                onClick={isUploading ? undefined : handleButtonClick}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  accept="image/*"
                />
                {isUploading ? (
                  <>
                    <div className="w-16 h-16 mx-auto mb-4 bg-secondary-100 rounded-full flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-500"></div>
                    </div>
                    <h3 className="font-medium text-text-heading mb-2">Uploading...</h3>
                    <p className="text-sm text-text-description mb-4 text-center">Please wait while your image is being processed</p>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 mx-auto mb-4 bg-secondary-100 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <h3 className="font-medium text-text-heading mb-2">Upload an image</h3>
                    <p className="text-sm text-text-description mb-4 text-center">Drag and drop or click to browse</p>
                    <p className="text-xs text-text-description">PNG, JPG, WebP up to 10MB</p>
                  </>
                )}
              </div>
            )}

            {uploadStatus && (
              <p className={`mt-3 text-sm ${uploadStatus.includes('successfully') ? 'text-green-600' :
                uploadStatus.includes('failed') || uploadStatus.includes('error') ? 'text-red-600' :
                  'text-blue-600'
                }`}>
                {uploadStatus}
              </p>
            )}
          </div>
        </div>

        {/* Second Column - Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col">
          <div className="p-4 border-b border-accent-taupe flex-shrink-0">
            <h2 className="text-lg font-semibold text-text-heading">Controls</h2>
            <span className="text-sm text-text-description">Configure your generation settings</span>
          </div>

          <div className="p-4 space-y-3 flex-1 overflow-y-auto">
            {/* Tool Selector and Video Settings in One Row */}
            <div>
              <div className="grid grid-cols-3 gap-2">
                {selectedTool !== 'upscaler' ? (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-text-heading mb-1">
                        Tool
                      </label>
                      <CustomSelect
                        value={selectedTool}
                        onChange={(value) => handleToolChange(value)}
                        options={toolOptions.map((option) => ({
                          value: option.value,
                          label: option.label,
                          disabled: option.disabled
                        }))}
                        className="w-full"
                      />
                    </div>

                    {selectedTool === 'video-generation' && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-text-heading mb-1">
                            Mode
                          </label>
                          <CustomSelect
                            value={selectedMode}
                            onChange={(value) => setSelectedMode(value)}
                            options={[
                              { value: 'performance', label: 'Performance' },
                              { value: 'balanced', label: 'Balanced' },
                              { value: 'quality', label: 'Quality' }
                            ]}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-text-heading mb-1">
                            Duration
                          </label>
                          <CustomSelect
                            value={selectedDuration.toString()}
                            onChange={(value) => setSelectedDuration(Number(value))}
                            disabled={!!selectedVideoTemplate}
                            options={[
                              { value: '5', label: '5sec' },
                              { value: '10', label: '10sec' }
                            ]}
                            className="w-full"
                          />
                        </div>
                      </>
                    )}

                    {selectedTool !== 'video-generation' && (
                      <>
                        <div></div>
                        <div></div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-text-heading mb-1">
                        Resolution
                      </label>
                      <CustomSelect
                        value={selectedResolution}
                        onChange={(value) => setSelectedResolution(value)}
                        options={[
                          { value: '2K', label: '2K' },
                          { value: '4K', label: '4K' },
                          { value: '8K', label: '8K' }
                        ]}
                        className="w-full"
                      />
                    </div>
                    <div></div>
                    <div></div>
                  </>
                )}
              </div>
              <p className="text-xs text-text-description mt-1">
                {getToolDescription(selectedTool)}
              </p>
            </div>

            {/* Video Generation Settings */}
            {selectedTool === 'video-generation' && (
              <>

                {/* Video Template Selector - Disabled when custom prompt is used */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-text-heading">
                      Video Templates
                    </label>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setSelectedVideoTemplate('');
                          setPrompt('');
                        }}
                        className="text-xs bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1 rounded-md transition-colors"
                        title="Clear template selection and prompt"
                      >
                        Clear
                      </button>
                      <span className="text-xs text-text-description">
                        {isLoadingTemplates ? 'Loading...' : `${videoTemplates.length} templates`}
                      </span>
                    </div>
                  </div>

                  {/* Loading state */}
                  {isLoadingTemplates && (
                    <div className="flex items-center justify-center p-4 border border-accent-taupe rounded-lg bg-accent-sand/50">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-secondary-500"></div>
                      <span className="ml-2 text-sm text-text-description">Loading...</span>
                    </div>
                  )}

                  {/* Error state */}
                  {templatesError && !isLoadingTemplates && (
                    <div className="p-3 border border-red-200 rounded-lg bg-red-50">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-xs text-red-700">Failed to load templates</p>
                      </div>
                      <p className="text-xs text-red-600 mt-1">{templatesError}</p>
                      <button
                        onClick={fetchVideoTemplates}
                        className="mt-2 text-xs bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1 rounded-md transition-colors"
                      >
                        Retry
                      </button>
                    </div>
                  )}

                  {/* Templates grid - Larger and more space */}
                  {!isLoadingTemplates && !templatesError && videoTemplates.length > 0 && (
                    <div>
                      {/* Show reason when templates are disabled by prompt */}
                      {prompt && prompt.trim() !== '' && (
                        <div className="mb-2 p-2 bg-orange-50 border border-orange-200 rounded-lg">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <p className="text-xs text-orange-700">
                              Video templates are disabled because you're using a custom prompt. Clear the prompt to enable template selection.
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-0 max-h-80 overflow-y-auto pr-1 border border-accent-taupe rounded-lg p-2 bg-accent-sand/50">
                        {videoTemplates.map((template) => (
                          <div
                            key={template.videoId}
                            className={`relative rounded-lg border-2 transition-all ${prompt && prompt.trim() !== ''
                              ? 'opacity-50 cursor-not-allowed border-accent-taupe'
                              : selectedVideoTemplate === template.videoId
                                ? 'border-secondary-500 ring-2 ring-secondary-200 cursor-pointer'
                                : 'border-accent-taupe hover:border-accent-taupe cursor-pointer'
                              }`}
                            style={{
                              transform: 'scale(0.8)',
                              transformOrigin: 'center',
                              margin: '-10%'
                            }}
                            onClick={() => {
                              // Don't allow interaction if prompt is active
                              if (prompt && prompt.trim() !== '') {
                                return;
                              }

                              // Toggle selection: deselect if already selected, select if not
                              if (selectedVideoTemplate === template.videoId) {
                                setSelectedVideoTemplate('');
                              } else {
                                setSelectedVideoTemplate(template.videoId);
                                // Clear prompt when template is selected
                                setPrompt('');
                              }
                            }}
                          >
                            <div className="relative overflow-hidden rounded-md" style={{ aspectRatio: '9/16' }}>
                              {/* Video thumbnail/preview */}
                              <video
                                src={template.videoUrl}
                                className="w-full h-full object-contain bg-black"
                                muted
                                loop
                                onPlay={() => {
                                  setPlayingVideos(prev => new Set(prev).add(template.videoId));
                                }}
                                onPause={() => {
                                  setPlayingVideos(prev => {
                                    const newSet = new Set(prev);
                                    newSet.delete(template.videoId);
                                    return newSet;
                                  });
                                }}
                                onEnded={() => {
                                  setPlayingVideos(prev => {
                                    const newSet = new Set(prev);
                                    newSet.delete(template.videoId);
                                    return newSet;
                                  });
                                }}
                              />

                              {/* Play/Pause button overlay */}
                              <div
                                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent double-triggering of parent click

                                  // Don't allow interaction if prompt is active
                                  if (prompt && prompt.trim() !== '') {
                                    return;
                                  }

                                  // Select template when play button is clicked
                                  if (selectedVideoTemplate !== template.videoId) {
                                    setSelectedVideoTemplate(template.videoId);
                                    setPrompt('');
                                  }

                                  const video = e.currentTarget.parentElement?.querySelector('video');
                                  if (video) {
                                    if (video.paused) {
                                      video.play();
                                    } else {
                                      video.pause();
                                    }
                                  }
                                }}
                              >
                                {playingVideos.has(template.videoId) ? (
                                  // Pause icon
                                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                                  </svg>
                                ) : (
                                  // Play icon
                                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M8 5v14l11-7z" />
                                  </svg>
                                )}
                              </div>

                              {/* Selection indicator */}
                              {selectedVideoTemplate === template.videoId && (
                                <div className="absolute top-1 right-1 bg-secondary-1000 text-white rounded-full p-1">
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                              )}
                            </div>

                            <div className="p-2">
                              <p className="text-xs font-medium text-text-heading truncate">Template {template.videoId}</p>
                              <p className="text-xs text-text-description truncate">Duration: {template.duration}sec, Type: {template.type}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Empty state */}
                  {!isLoadingTemplates && !templatesError && videoTemplates.length === 0 && (
                    <div className="text-center p-6 border border-accent-taupe rounded-lg bg-accent-sand/50">
                      <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <p className="text-xs text-text-description">No video templates available</p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Prompt Section */}
            {selectedTool !== 'upscaler' && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-text-heading">
                    Prompt {selectedTool === 'video-generation' && selectedVideoTemplate ? '(Disabled - using video template)' : ''}
                  </label>
                  {!(selectedTool === 'video-generation' && selectedVideoTemplate) && (
                    <button
                      onClick={handleAutogeneratePrompt}
                      disabled={isGeneratingPrompt}
                      className={`text-xs px-2 py-1 rounded-md transition-colors ${isGeneratingPrompt
                        ? 'bg-accent-sand text-text-description cursor-not-allowed'
                        : 'bg-secondary-100 hover:bg-secondary-200 text-secondary-700'
                        }`}
                      title={isGeneratingPrompt ? "Generating prompt..." : "Generate AI prompt based on image"}
                    >
                      {isGeneratingPrompt ? (
                        <div className="flex items-center space-x-1">
                          <div className="animate-spin rounded-full h-3 w-3 border-b border-secondary-500"></div>
                          <span>Generating...</span>
                        </div>
                      ) : (
                        'Auto-generate'
                      )}
                    </button>
                  )}
                </div>
                <textarea
                  value={prompt}
                  onChange={(e) => {
                    setPrompt(e.target.value);
                    // Clear template selection when user starts typing a custom prompt
                    if (e.target.value.trim() !== '' && selectedVideoTemplate) {
                      setSelectedVideoTemplate('');
                    }
                  }}
                  disabled={selectedTool === 'background-removal' || (selectedTool === 'video-generation' && !!selectedVideoTemplate)}
                  placeholder={
                    selectedTool === 'background-removal'
                      ? 'Prompt is not needed for background removal'
                      : selectedTool === 'video-generation' && selectedVideoTemplate
                        ? 'Prompt is disabled when using video templates'
                        : 'Describe the video animation style you want...'
                  }
                  className={`w-full border border-accent-taupe rounded-lg px-3 py-2 text-sm focus:ring-secondary-500 focus:border-secondary-500 min-h-[80px] resize-y ${selectedTool === 'background-removal' || (selectedTool === 'video-generation' && selectedVideoTemplate)
                    ? 'bg-accent-sand text-text-description cursor-not-allowed'
                    : ''
                    }`}
                />
                {selectedTool === 'video-generation' && (
                  <p className="text-xs text-text-description mt-1">
                    {selectedVideoTemplate
                      ? 'Using selected video template - clear template selection to enable custom prompt'
                      : prompt && prompt.trim() !== '' && prompt !== 'showing off clothes to camera in different poses'
                        ? 'Custom prompt will override video templates'
                        : 'Select a video template or enter a custom prompt'
                    }
                  </p>
                )}
              </div>
            )}

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={!inputImage || isProcessing}
              className={`w-full px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${!inputImage || isProcessing
                ? 'bg-gray-300 text-text-description cursor-not-allowed'
                : 'bg-secondary-500 hover:bg-secondary-600 text-white'
                }`}
            >
              {isProcessing ? (
                <div className="flex items-center justify-center space-x-2">
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  <span>Processing...</span>
                  <div className="flex items-center space-x-1 opacity-90">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-mono font-semibold text-xs">
                      {generationTime.toFixed(1)}s
                    </span>
                  </div>
                </div>
              ) : (
                'Generate'
              )}
            </button>

            {/* Credit Cost Preview */}
            <div className="bg-accent-sand rounded-lg p-2">
              <CreditCostPreview
                creditCost={currentCreditEstimation.creditCost}
                isEstimating={currentCreditEstimation.isEstimating}
                error={currentCreditEstimation.error}
                canAfford={currentCreditEstimation.canAfford}
                className="text-xs"
              />
            </div>
          </div>
        </div>

        {/* Third Column - Generated Result */}
        <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col">
          <div className="p-4 border-b border-accent-taupe flex-shrink-0">
            <h2 className="text-lg font-semibold text-text-heading">Generated Result</h2>
            <span className="text-sm text-text-description">
              {selectedTool === 'video-generation' ? 'Generated video will appear here' : 'Processed image will appear here'}
            </span>
          </div>

          <div className="p-4 flex-1 flex flex-col">
            {isProcessing ? (
              <div className="flex-1 bg-accent-sand rounded-lg border border-accent-taupe flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-500 mx-auto mb-4"></div>
                  <p className="text-sm text-text-description">Processing your image...</p>
                  <p className="text-xs text-text-description mt-2">This may take a few moments</p>
                </div>
              </div>
            ) : generatedResult ? (
              <div className="relative flex-1">
                {selectedTool === 'video-generation' ? (
                  generatedResult ? (
                    <>
                      <video
                        src={generatedResult}
                        controls
                        preload="metadata"
                        playsInline
                        className="w-full h-full object-contain bg-white rounded-lg border border-accent-taupe"
                        onLoadStart={() => {
                          console.log('Video loading started:', generatedResult);
                          setIsVideoLoading(true);
                        }}
                        onCanPlay={() => {
                          console.log('Video can play:', generatedResult);
                          setIsVideoLoading(false);
                        }}
                        onError={(e) => {
                          console.error('Video failed to load:', generatedResult);
                          setIsVideoLoading(false);
                          // Show error state if video fails to load
                          const target = e.target as HTMLVideoElement;
                          target.style.display = 'none';
                          const errorDiv = target.nextElementSibling as HTMLElement;
                          if (errorDiv) {
                            errorDiv.style.display = 'flex';
                          }
                        }}
                      >
                        Your browser does not support the video tag.
                      </video>
                      {isVideoLoading && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                          <div className="text-center text-white">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                            <p className="text-sm">Loading video...</p>
                          </div>
                        </div>
                      )}
                      <div
                        className="w-full h-full bg-gray-900 rounded-lg border border-accent-taupe items-center justify-center"
                        style={{ display: 'none' }}
                      >
                        <div className="text-center text-white p-8">
                          <svg className="w-12 h-12 mx-auto mb-4 text-red-400" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                          </svg>
                          <p className="text-sm mb-2">Video failed to load</p>
                          <p className="text-xs text-gray-300 mb-4">The video may be processing or the URL may be invalid</p>
                          <a
                            href={generatedResult}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 bg-secondary-500 text-white text-sm rounded-lg hover:bg-secondary-600 transition-colors"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download Video
                          </a>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full bg-gray-900 rounded-lg border border-accent-taupe flex items-center justify-center">
                      <div className="text-center text-white">
                        <svg className="w-12 h-12 mx-auto mb-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                        <p className="text-sm">Video preview will be available soon</p>
                        <p className="text-xs text-gray-300 mt-2">Generated video: video.mp4</p>
                      </div>
                    </div>
                  )
                ) : (
                  <img
                    src={generatedResult}
                    alt="Generated result"
                    className="w-full h-full object-contain bg-accent-sand rounded-lg border border-accent-taupe"
                  />
                )}
                <div className="absolute top-2 right-2">
                  <button
                    className="bg-secondary-500 hover:bg-secondary-600 text-white rounded-full p-2 shadow-lg"
                    title={selectedTool === 'video-generation' ? "Download video" : "Download result"}
                    onClick={async () => {
                      if (selectedTool === 'video-generation') {
                        // Download video with Firefox compatibility
                        try {
                          const response = await fetch(generatedResult);
                          const blob = await response.blob();
                          const blobUrl = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = blobUrl;
                          link.download = `canvas-editor-video.mp4`;
                          link.style.display = 'none';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          // Cleanup blob URL to free memory
                          window.URL.revokeObjectURL(blobUrl);
                        } catch (error) {
                          console.error('Download failed:', error);
                          // Fallback to opening in new tab if download fails
                          window.open(generatedResult, '_blank');
                        }
                      } else if (selectedTool === 'upscaler') {
                        // Download upscaled image with blob approach for better compatibility
                        try {
                          const response = await fetch(generatedResult);
                          const blob = await response.blob();
                          const blobUrl = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = blobUrl;
                          link.download = `upscaled-image.jpg`;
                          link.style.display = 'none';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          // Cleanup blob URL to free memory
                          window.URL.revokeObjectURL(blobUrl);
                        } catch (error) {
                          console.error('Download failed:', error);
                          // Fallback to opening in new tab if download fails
                          window.open(generatedResult, '_blank');
                        }
                      } else {
                        // Download other results (keeping existing method for other tools)
                        const link = document.createElement('a');
                        link.href = generatedResult;
                        link.download = `canvas-editor-result.jpg`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
                    </svg>
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1 bg-accent-sand rounded-lg border border-accent-taupe flex items-center justify-center">
                <div className="text-center text-text-description">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm">Upload an image and click Generate to see results</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>



      {/* Subscription Guard Modal */}
      {showSubscriptionGuard && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowSubscriptionGuard(false)}
        >
          <div
            className="bg-white rounded-lg w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-2xl xl:max-w-4xl max-h-[90vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button - always visible */}
            <button
              onClick={() => setShowSubscriptionGuard(false)}
              className="absolute top-4 right-4 z-10 bg-accent-sand hover:bg-accent-taupe text-text-description rounded-full p-2 transition-colors"
              title="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <SubscriptionGuard
              requiredPlans={[SubscriptionType.PRO, SubscriptionType.ENTERPRISE]}
              featureName={
                selectedTool === 'video-generation'
                  ? 'Canvas Editor - Video Generation'
                  : 'Canvas Editor - Background Removal'
              }
            >
              <></>
            </SubscriptionGuard>
          </div>
        </div>
      )}

      {/* Insufficient Credits Modal */}
      <InsufficientCreditsModal
        isOpen={showInsufficientCreditsModal}
        onClose={() => setShowInsufficientCreditsModal(false)}
        requiredCredits={currentCreditEstimation.creditCost || 0}
        availableCredits={userCredits}
        featureName="Canvas Editor"
      />
    </div>
  );
};

export default CanvasEditor; 